// src/components/QuoteWidget.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';

const QuoteWidget = React.memo(function QuoteWidget({
  useQuoteApi,
  fx,
  className = "",
  onOpenMaterials,
  onOpenEquipment,
  onOpenServices,
  onOpenControls,
}) {
  const { t, i18n } = useTranslation();
  const { totals, currency, setCurrency, quote, setEuroRate } = useQuoteApi;

  const [open, setOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenBtnRef = useRef(null);

  // Pretpostavimo da su sve cene u quote-u u RSD
  const baseCurrency = 'RSD';
  const targetCurrency = useMemo(() => 
    String(currency || 'RSD').toUpperCase(), [currency]
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => setOpen(mq.matches);
    apply();
    
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    } else if (mq.addListener) {
      mq.addListener(apply);
      return () => mq.removeListener(apply);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => console.error('FS error:', err));
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Memoizovana funkcija za formatiranje brojeva
  const fmt = useCallback((v) => {
    const locale = i18n.language || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    return new Intl.NumberFormat(locale, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(v);
  }, [i18n.language]);

  // Ispravljena funkcija za konverziju valuta
  const convert = useCallback((value, from, to) => {
    const src = String(from || 'RSD').toUpperCase();
    const dst = String(to || 'RSD').toUpperCase();
    
    if (!Number.isFinite(value) || src === dst) return value;

    // Ako su cene u RSD, a želimo ih prikazati u RSD
    if (src === 'RSD' && dst === 'RSD') return value;
    
    // Ako su cene u RSD, a želimo ih prikazati u EUR
    if (src === 'RSD' && dst === 'EUR') {
      const eurPerRsd = Number(quote?.euro_rate ?? 117);
      return value ;
    }
    
    // Ako su cene u RSD, a želimo ih prikazati u drugoj valuti (npr. USD)
    if (src === 'RSD' && dst !== 'EUR') {
      const eurPerRsd = Number(quote?.euro_rate ?? 117);
      const eurValue = value / eurPerRsd;
      const rateToTarget = Number(fx?.rates?.[dst]);
      return Number.isFinite(rateToTarget) ? eurValue * rateToTarget : NaN;
    }
    
    // Ako su cene u EUR, a želimo ih prikazati u RSD
    if (src === 'EUR' && dst === 'RSD') {
      const eurPerRsd = Number(quote?.euro_rate ?? 117);
      return value * eurPerRsd;
    }
    
    // Ako su cene u EUR, a želimo ih prikazati u drugoj valuti
    if (src === 'EUR' && dst !== 'RSD') {
      const rateToTarget = Number(fx?.rates?.[dst]);
      return Number.isFinite(rateToTarget) ? value * rateToTarget : NaN;
    }
    
    // Konverzija između drugih valuta preko EUR
    if (src !== 'RSD' && src !== 'EUR') {
      const rateToEur = 1 / Number(fx?.rates?.[src]);
      const eurValue = value * rateToEur;
      
      if (dst === 'EUR') return eurValue;
      if (dst === 'RSD') {
        const eurPerRsd = Number(quote?.euro_rate ?? 117);
        return eurValue * eurPerRsd;
      }
      
      const rateToTarget = Number(fx?.rates?.[dst]);
      return Number.isFinite(rateToTarget) ? eurValue * rateToTarget : NaN;
    }
    
    return NaN;
  }, [quote?.euro_rate, fx?.rates]);

  // Memoizovana vrednost footerRateStr - sada prikazuje odnos EUR prema izabranoj valuti
const footerRateStr = useMemo(() => {
  if (targetCurrency === 'RSD') {
    const eurPerRsd = Number(quote?.euro_rate ?? 117);
    return fmt(eurPerRsd);
  } else if (targetCurrency === 'EUR') {
    return fmt(1);
  } else {
    const rateToTarget = Number(fx?.rates?.[targetCurrency]);
    return Number.isFinite(rateToTarget) ? fmt(rateToTarget) : '—';
  }
}, [targetCurrency, quote?.euro_rate, fx?.rates, fmt]);

  // Memoizovana funkcija za proveru kursa
  const hasRate = useCallback((cc) => {
    if (!cc) return false;
    const u = String(cc).toUpperCase();
    if (u === 'RSD') return true;
    if (u === 'EUR') return true;
    return Number.isFinite(Number(fx?.rates?.[u]));
  }, [fx?.rates]);

  // Memoizovane opcije valuta
  const options = useMemo(() => {
    const popularFor = (curr, lang) => {
      const L = String(lang || 'en').slice(0, 2);
      const byLang = {
        sr: ['EUR','RSD','USD','CHF','GBP','CNY'],
        ru: ['RUB','EUR','USD','GBP','CNY'],
        cs: ['CZK','EUR','USD','GBP','CHF'],
        hu: ['HUF','EUR','USD','GBP','CHF'],
        pl: ['PLN','EUR','USD','GBP','CHF'],
        ro: ['RON','EUR','USD','GBP','CHF'],
        zh: ['CNY','USD','EUR','HKD','JPY'],
        de: ['EUR','USD','GBP','CHF','CNY','PLN'],
        it: ['EUR','USD','GBP','CHF','CNY'],
        es: ['EUR','USD','GBP','CHF','CNY'],
        fr: ['EUR','USD','GBP','CHF','CNY'],
        nl: ['EUR','USD','GBP','CHF','CNY'],
        pt: ['EUR','USD','GBP','CHF','BRL'],
        default: ['EUR','RSD','USD','GBP','CHF','CNY']
      };
      const base = byLang[L] || byLang.default;
      const first = String(curr).toUpperCase();
      const uniq = [first, ...base.filter(c => c && c.toUpperCase() !== first)];
      return uniq.slice(0, 8);
    };
    
    return popularFor(targetCurrency, i18n.language);
  }, [targetCurrency, i18n.language]);

  const materialsText = t('quote.materials') || 'Materijali';

  // Memoizovani elementi za prikaz
  const currencySelect = useMemo(() => (
    <div className="relative h-10">
      <select
        value={currency}
        onChange={(e) => {
          const next = e.target.value.toUpperCase();
          if (!hasRate(next)) {
            console.warn(`No FX rate for ${next}`);
            return;
          }
          setCurrency(next);
        }}
        className="h-10 w-24 rounded-md bg-black/60 text-white shadow
                   pl-3 pr-8 appearance-none leading-none
                   hover:bg-black/70 transition
                   focus:outline-none focus:ring-2 focus:ring-white/20"
        title={t('quote.changeCurrency')}
      >
        {options.map(c => (
          <option key={c} value={c} disabled={!hasRate(c)}>
            {c}{!hasRate(c) ? ' (—)' : ''}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
        width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  ), [currency, options, hasRate, setCurrency, t]);

  return (
    <div className={`w-full md:w-72 md:max-w-[18rem] rounded-xl bg-neutral-700/80 text-white shadow-xl backdrop-blur-sm ${className} relative`}>
      <div className="flex items-center justify-between px-4 pt-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onOpenControls?.('light')}
            className="h-10 w-10 inline-flex items-center justify-center rounded-md
                       bg-black/60 text-white hover:bg-black/70 shadow transition"
            aria-label="Tools" title="Tools"
          >
            ⚙️
          </button>
        </div>

        <div className="text-lg font-extrabold" />

        {currencySelect}
      </div>

      <button
        className="w-full mt-2 px-4 pb-2 flex items-center justify-between gap-3"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="quote-details"
        title={t('quote.toggleDetails')}
      >
        <span className="text-sm font-bold opacity-90">{t('quote.grandTotal')}</span>
        <span className="text-xl font-extrabold tracking-wide">
          {fmt(convert(totals.grand.total, baseCurrency, targetCurrency))} <span className="text-sm font-bold">{targetCurrency}</span>
        </span>
        <svg className={`ml-1 h-4 w-4 shrink-0 transition-transform md:hidden ${open ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="CurrentColor">
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
        </svg>
      </button>

      <div
        id="quote-details"
        className={`px-4 pb-3 border-t border-white/10 transition-all overflow-hidden
          ${open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"} md:max-h-96 md:opacity-100`}
      >
        <div className="flex justify-between items-center py-2 text-sm">
          {onOpenMaterials ? (
            <button
              onClick={onOpenMaterials}
              className="px-2 py-1 rounded-md bg-primary-light text-white dark:text-black font-medium shadow-sm"
              title={t('quote.changeMaterials')}
            >
              {materialsText}
            </button>
          ) : (
            <span>{t('quote.materials')}</span>
          )}
          <span>
            {fmt(convert(totals.materials.total, baseCurrency, targetCurrency))} {targetCurrency}
          </span>
        </div>

        <div className="flex justify-between items-center py-2 text-sm">
          {onOpenEquipment ? (
            <button
              onClick={onOpenEquipment}
              className="px-2 py-1 rounded-md bg-primary-light text-white dark:text-black font-medium shadow-sm"
              title={t('quote.changeEquipment')}
            >
              {t('quote.equipment')}
            </button>
          ) : (
            <span>{t('quote.equipment')}</span>
          )}
          <span>
            {fmt(convert(totals.equipment.total, baseCurrency, targetCurrency))} {targetCurrency}
          </span>
        </div>

        <div className="flex justify-between items-center py-2 text-sm">
          {onOpenServices ? (
            <button
              onClick={onOpenServices}
              className="px-2 py-1 rounded-md bg-primary-light text-white dark:text-black font-medium shadow-sm"
              title={t('quote.changeServices')}
            >
              {t('quote.services')}
            </button>
          ) : (
            <span>{t('quote.services')}</span>
          )}
          <span>
            {fmt(convert(totals.services.total, baseCurrency, targetCurrency))} {targetCurrency}
          </span>
        </div>

        <div className="mt-2 pt-2 text-xs text-neutral-400 border-t border-white/10">
  {targetCurrency === 'RSD' ? (
    <>1 EUR = {footerRateStr} RSD</>
  ) : targetCurrency === 'EUR' ? (
    <>1 EUR = 1 EUR</>
  ) : (
    <>1 EUR = {footerRateStr} {targetCurrency}</>
  )}
</div>
        <div className="text-xs text-neutral-400 mt-2">
          <Trans
            i18nKey="quote.priceDisclaimer"
            components={[
              <a
                href="mailto:sales@modularobjects.com"
                className="text-primary-light underline underline-offset-2 hover:opacity-80"
              />
            ]}
          />
        </div>
      </div>
    </div>
  );
});

export default QuoteWidget;

export function useQuote(initialQuote) {
  const [quote, setQuote] = useState(initialQuote);
  const [currency, setCurrency] = useState(initialQuote?.currency || 'RSD');

  return {
    quote,
    setQuote,
    currency,
    setCurrency,
    totals: {
      materials: quote.materials ?? { total: 0 },
      equipment: quote.equipment ?? { total: 0 },
      services:  quote.services  ?? { total: 0 },
      grand:     { total: quote.grand_total ?? 0 },
    },
    setEuroRate,
  };
}